import React from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Search, Layers } from "lucide-react";
import Shell from "../components/Shell";
import ProjectGridCard from "../components/ProjectGridCard";
import { TagChip } from "../components/bits";
import { endpoints } from "../lib/api";
import { ECOSYSTEM, TRACK_TAGS } from "../lib/tagMeta";
import * as store from "../lib/storage";

const TRACKS = [{ k: "all", label: "All" }, { k: "AI", label: "AI" }, { k: "Web3", label: "Web3" }, { k: "both", label: "AI + Web3" }];

export default function ExplorePage() {
  const [sp, setSp] = useSearchParams();
  const navigate = useNavigate();
  const [projects, setProjects] = React.useState([]);
  const [tags, setTags] = React.useState([]);
  const [loading, setLoading] = React.useState(true);

  const track = sp.get("track") || "all";
  const selTags = sp.getAll("tag");
  const q = sp.get("q") || "";
  const [search, setSearch] = React.useState(q);

  const update = (next) => {
    const p = new URLSearchParams(sp);
    Object.entries(next).forEach(([k, v]) => {
      p.delete(k);
      if (Array.isArray(v)) v.forEach((x) => p.append(k, x));
      else if (v) p.set(k, v);
    });
    setSp(p);
  };

  React.useEffect(() => { endpoints.tags().then(setTags).catch(() => {}); }, []);
  React.useEffect(() => {
    setLoading(true);
    const params = { session_id: store.getSessionShuffle() };
    if (track !== "all") params.track = track;
    if (selTags.length) params.tags = selTags.join(",");
    if (q) params.q = q;
    endpoints.projects(params).then((d) => { setProjects(d.projects); setLoading(false); }).catch(() => setLoading(false));
  }, [track, q, selTags.join(",")]); // eslint-disable-line

  const toggleTag = (tag) => update({ tag: selTags.includes(tag) ? selTags.filter((t) => t !== tag) : [...selTags, tag] });

  const orderedTags = React.useMemo(() => tags.filter((t) => !TRACK_TAGS.includes(t.tag)).sort((a, b) => {
    const ea = ECOSYSTEM.includes(a.tag), eb = ECOSYSTEM.includes(b.tag);
    if (ea !== eb) return ea ? -1 : 1; return b.count - a.count;
  }), [tags]);

  return (
    <Shell>
      <div className="mx-auto w-full max-w-6xl px-4 py-6">
        <h1 className="font-display text-[28px] font-extrabold text-white">Browse all projects</h1>
        <p className="mb-5 text-on-dark-muted">99 projects built at U-Hackathon 2026.</p>

        <form onSubmit={(e) => { e.preventDefault(); update({ q: search }); }} className="relative mb-4 max-w-xl">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-400" />
          <input data-testid="explore-search" value={search} onChange={(e) => setSearch(e.target.value)}
            className="w-full rounded-pill border border-white/15 bg-white/5 py-2.5 pl-9 pr-3 text-[15px] text-white outline-none placeholder:text-ink-400 focus:border-gold-500"
            placeholder="Search projects, teams or tech" />
        </form>

        <div className="mb-3 flex flex-wrap gap-2">
          {TRACKS.map((t) => (
            <button key={t.k} data-testid={`explore-track-${t.k}`} onClick={() => update({ track: t.k === "all" ? "" : t.k })}
              className={`rounded-pill border px-4 py-1.5 text-[13px] font-bold ${track === t.k ? "border-gold-500 bg-gold-500 text-navy-900" : "border-white/15 bg-white/5 text-white"}`}>
              {t.label}
            </button>
          ))}
        </div>

        <div className="no-scrollbar mb-6 flex flex-wrap gap-2">
          {orderedTags.map((t) => <TagChip key={t.tag} tag={t.tag} selected={selTags.includes(t.tag)} onClick={() => toggleTag(t.tag)} />)}
        </div>

        {loading ? (
          <p className="text-on-dark-muted">Loading…</p>
        ) : projects.length === 0 ? (
          <div className="flex flex-col items-center gap-3 py-16 text-center">
            <Layers size={40} className="text-ink-400" />
            <p className="text-on-dark-muted">No matches… yet. Try removing a filter.</p>
          </div>
        ) : (
          <>
            {selTags.length > 0 && (
              <button onClick={() => navigate(`/deck?${selTags.map((t) => `tag=${encodeURIComponent(t)}`).join("&")}${track !== "all" ? `&track=${track}` : ""}`)}
                className="mb-4 rounded-pill bg-violet-500 px-5 py-2 font-bold text-white" data-testid="swipe-these">Swipe these →</button>
            )}
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
              {projects.map((p) => <ProjectGridCard key={p.id} project={p} />)}
            </div>
            <p className="mt-6 text-center text-[13px] text-on-dark-muted">{projects.length} projects</p>
          </>
        )}
      </div>
    </Shell>
  );
}
